import type { ResolutionStatus, VisioReviewConfig, VisioReviewInteraction } from '@gozoki/api-types'
import {
  type QueryClient,
  skipToken,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  saveManagerEvaluation,
  updateVisioReviewConfig,
  updateVisioReviewResolution,
  validateVisioReviewAiGrade,
} from '../mutations/visioReviews.mutations'
import {
  fetchVisioReviewConfig,
  fetchVisioReviewFilterOptions,
  type FetchVisioReviewFilterOptionsParams,
  fetchVisioReviewGlobalCharts,
  type FetchVisioReviewGlobalChartsParams,
  fetchVisioReviewInteractions,
  fetchVisioReviewInteractionsToEvaluate,
  type FetchVisioReviewInteractionsToEvaluateParams,
  fetchVisioReviewOperatorActivityHeatmap,
  fetchVisioReviewOperatorCharts,
  fetchVisioReviewOperatorDetail,
  fetchVisioReviewOperatorInteractions,
  fetchVisioReviewOperators,
  fetchVisioReviewRecordingUrl,
  fetchVisioReviewStoreActivityHeatmap,
  fetchVisioReviewStoreCharts,
  fetchVisioReviewStoreDetail,
  fetchVisioReviewStoreInteractions,
  fetchVisioReviewStoreList,
  fetchVisioReviewStoreOperators,
  fetchVisioReviewTranscript,
  type FetchVisioReviewActivityHeatmapParams,
  type FetchVisioReviewInteractionsParams,
  type FetchVisioReviewOperatorsParams,
  type FetchVisioReviewStoreChartsParams,
  type FetchVisioReviewStoreListParams,
} from '../queries/visioReviews.queries'

const STALE_TIME = 120_000

const hasMorePageParam = <T extends { hasMorePage: boolean }>(lastPage: T, allPages: T[]) =>
  lastPage.hasMorePage ? allPages.length + 1 : undefined

export const useVisioReviewInteractions = (params: FetchVisioReviewInteractionsParams) =>
  useInfiniteQuery({
    queryKey: ['visio-reviews', 'interactions', params],
    queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
      fetchVisioReviewInteractions({ ...params, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: hasMorePageParam,
    staleTime: STALE_TIME,
  })

export const useVisioReviewOperators = (params: FetchVisioReviewOperatorsParams) =>
  useInfiniteQuery({
    queryKey: ['visio-reviews', 'operators', params],
    queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
      fetchVisioReviewOperators({ ...params, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: hasMorePageParam,
    staleTime: STALE_TIME,
  })

export const useVisioReviewOperatorCharts = (params: {
  dateStart?: string
  dateEnd: string
  kioskTypes?: string
  onlyVerified?: boolean
}) =>
  useQuery({
    queryKey: ['visio-reviews', 'operators', 'charts', params],
    queryFn: () => fetchVisioReviewOperatorCharts(params),
    staleTime: STALE_TIME,
  })

export const useVisioReviewGlobalCharts = (params: FetchVisioReviewGlobalChartsParams) =>
  useQuery({
    queryKey: ['visio-reviews', 'global', 'charts', params],
    queryFn: () => fetchVisioReviewGlobalCharts(params),
    staleTime: STALE_TIME,
  })

export const useVisioReviewOperatorActivityHeatmap = (
  adminId: number | null,
  params: FetchVisioReviewActivityHeatmapParams,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: ['visio-reviews', 'operator-activity-heatmap', adminId, params],
    queryFn: adminId ? () => fetchVisioReviewOperatorActivityHeatmap(adminId, params) : skipToken,
    enabled: (options?.enabled ?? true) && adminId !== null,
    staleTime: STALE_TIME,
  })

export const useVisioReviewStoreActivityHeatmap = (
  storeId: number | null,
  params: FetchVisioReviewActivityHeatmapParams,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: ['visio-reviews', 'store-activity-heatmap', storeId, params],
    queryFn: storeId ? () => fetchVisioReviewStoreActivityHeatmap(storeId, params) : skipToken,
    enabled: (options?.enabled ?? true) && storeId !== null,
    staleTime: STALE_TIME,
  })

export const useVisioReviewOperatorDetail = (
  adminId: number | null,
  params: { dateStart?: string; dateEnd: string; kioskTypes?: string; onlyVerified?: boolean },
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: ['visio-reviews', 'operator-detail', adminId, params],
    queryFn: adminId ? () => fetchVisioReviewOperatorDetail(adminId, params) : skipToken,
    enabled: (options?.enabled ?? true) && adminId !== null,
    staleTime: STALE_TIME,
  })

export const useVisioReviewOperatorInteractions = (
  adminId: number | null,
  params: { dateStart?: string; dateEnd: string; kioskTypes?: string; onlyVerified?: boolean },
  options?: { enabled?: boolean; pageSize?: number },
) =>
  useInfiniteQuery({
    queryKey: ['visio-reviews', 'operator-interactions', adminId, params, options?.pageSize],
    queryFn: adminId
      ? ({ pageParam = 1 }: { pageParam?: number }) =>
          fetchVisioReviewOperatorInteractions(adminId, {
            ...params,
            page: pageParam,
            pageSize: options?.pageSize ?? 5,
          })
      : skipToken,
    initialPageParam: 1,
    getNextPageParam: hasMorePageParam,
    enabled: (options?.enabled ?? true) && adminId !== null,
    staleTime: STALE_TIME,
  })

export const useVisioReviewStoreList = (params: Omit<FetchVisioReviewStoreListParams, 'page'>) =>
  useInfiniteQuery({
    queryKey: ['visio-reviews', 'stores', params],
    queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
      fetchVisioReviewStoreList({ ...params, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: hasMorePageParam,
    staleTime: STALE_TIME,
  })

export const useVisioReviewStoreCharts = (params: FetchVisioReviewStoreChartsParams) =>
  useQuery({
    queryKey: ['visio-reviews', 'stores', 'charts', params],
    queryFn: () => fetchVisioReviewStoreCharts(params),
    staleTime: STALE_TIME,
  })

export const useVisioReviewStoreDetail = (
  storeId: number | null,
  params: { dateStart?: string; dateEnd: string; kioskTypes?: string; onlyVerified?: boolean },
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: ['visio-reviews', 'store-detail', storeId, params],
    queryFn: storeId ? () => fetchVisioReviewStoreDetail(storeId, params) : skipToken,
    enabled: (options?.enabled ?? true) && storeId !== null,
    staleTime: STALE_TIME,
  })

export const useVisioReviewStoreInteractions = (
  storeId: number | null,
  params: { dateStart?: string; dateEnd: string; kioskTypes?: string; onlyVerified?: boolean },
  options?: { enabled?: boolean; pageSize?: number },
) =>
  useInfiniteQuery({
    queryKey: ['visio-reviews', 'store-interactions', storeId, params, options?.pageSize],
    queryFn: storeId
      ? ({ pageParam = 1 }: { pageParam?: number }) =>
          fetchVisioReviewStoreInteractions(storeId, {
            ...params,
            page: pageParam,
            pageSize: options?.pageSize ?? 5,
          })
      : skipToken,
    initialPageParam: 1,
    getNextPageParam: hasMorePageParam,
    enabled: (options?.enabled ?? true) && storeId !== null,
    staleTime: STALE_TIME,
  })

export const useVisioReviewStoreOperators = (
  storeId: number | null,
  params: { dateStart?: string; dateEnd: string; kioskTypes?: string; onlyVerified?: boolean },
  options?: { enabled?: boolean; pageSize?: number },
) =>
  useInfiniteQuery({
    queryKey: ['visio-reviews', 'store-operators', storeId, params, options?.pageSize],
    queryFn: storeId
      ? ({ pageParam = 1 }: { pageParam?: number }) =>
          fetchVisioReviewStoreOperators(storeId, {
            ...params,
            page: pageParam,
            pageSize: options?.pageSize ?? 5,
          })
      : skipToken,
    initialPageParam: 1,
    getNextPageParam: hasMorePageParam,
    enabled: (options?.enabled ?? true) && storeId !== null,
    staleTime: STALE_TIME,
  })

// Returns a short-lived presigned S3 URL for the recorded visio. The URL
// expires (~15 min) so we treat the cache entry as always-stale: reopening
// the modal triggers a fresh fetch instead of handing back a dead signature.
export const useVisioReviewRecordingUrl = (reviewId: number | null) =>
  useQuery({
    queryKey: ['visio-reviews', 'recording-url', reviewId],
    queryFn: reviewId ? () => fetchVisioReviewRecordingUrl(reviewId) : skipToken,
    enabled: reviewId !== null,
    staleTime: 0,
    gcTime: 10 * 60 * 1000, // 10 min — below the 15-min presigned URL TTL
    retry: false,
  })

// Transcripts can be populated asynchronously server-side after the visio
// ends; treat the cache entry as always-stale so reopening the section picks
// up a transcript that just became available instead of a stale `null`.
export const useVisioReviewTranscript = (reviewId: number | null) =>
  useQuery({
    queryKey: ['visio-reviews', 'transcript', reviewId],
    queryFn: reviewId ? () => fetchVisioReviewTranscript(reviewId) : skipToken,
    enabled: reviewId !== null,
    staleTime: 0,
  })

interface OperatorInteractionsPage {
  rows: VisioReviewInteraction[]
  hasMorePage: boolean
}
interface OperatorInteractionsCache {
  pages: OperatorInteractionsPage[]
  pageParams: unknown[]
}

const patchInteractionsCache = (
  queryClient: QueryClient,
  queryKeyHead: string,
  reviewId: number,
  patch: Partial<VisioReviewInteraction>,
) => {
  queryClient.setQueriesData<OperatorInteractionsCache>(
    { queryKey: ['visio-reviews', queryKeyHead] },
    (old) => {
      if (!old) return old
      return {
        ...old,
        pages: old.pages.map((page) => ({
          ...page,
          rows: page.rows.map((row) => (row.id === reviewId ? { ...row, ...patch } : row)),
        })),
      }
    },
  )
}

const patchOperatorInteractionsCache = (
  queryClient: QueryClient,
  reviewId: number,
  patch: Partial<VisioReviewInteraction>,
) => {
  patchInteractionsCache(queryClient, 'operator-interactions', reviewId, patch)
  patchInteractionsCache(queryClient, 'store-interactions', reviewId, patch)
}

const invalidateVisioReviewData = (queryClient: QueryClient) => {
  // Immediately refetch the active evaluation views
  queryClient.invalidateQueries({ queryKey: ['visio-reviews', 'interactions'] })
  queryClient.invalidateQueries({ queryKey: ['visio-reviews', 'to-evaluate'] })
  // Mark others as stale without triggering an immediate refetch
  queryClient.invalidateQueries({
    queryKey: ['visio-reviews', 'operators'],
    refetchType: 'none',
  })
  queryClient.invalidateQueries({
    queryKey: ['visio-reviews', 'operator-detail'],
    refetchType: 'none',
  })
  queryClient.invalidateQueries({
    queryKey: ['visio-reviews', 'operator-interactions'],
    refetchType: 'none',
  })
  queryClient.invalidateQueries({
    queryKey: ['visio-reviews', 'store-detail'],
    refetchType: 'none',
  })
  queryClient.invalidateQueries({
    queryKey: ['visio-reviews', 'store-interactions'],
    refetchType: 'none',
  })
  queryClient.invalidateQueries({
    queryKey: ['visio-reviews', 'store-operators'],
    refetchType: 'none',
  })
}

export const useSaveManagerEvaluation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      reviewId,
      data,
    }: {
      reviewId: number
      data: { managerGrade: number; managerComment?: string }
    }) => saveManagerEvaluation(reviewId, data),
    onMutate: async ({ reviewId, data }) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: ['visio-reviews', 'operator-interactions'] }),
        queryClient.cancelQueries({ queryKey: ['visio-reviews', 'store-interactions'] }),
      ])
      const previous = [
        ...queryClient.getQueriesData<OperatorInteractionsCache>({
          queryKey: ['visio-reviews', 'operator-interactions'],
        }),
        ...queryClient.getQueriesData<OperatorInteractionsCache>({
          queryKey: ['visio-reviews', 'store-interactions'],
        }),
      ]
      patchOperatorInteractionsCache(queryClient, reviewId, {
        managerGrade: data.managerGrade,
        managerComment: data.managerComment ?? null,
      })
      return { previous }
    },
    onError: (_err, _vars, context) => {
      context?.previous.forEach(([key, value]) => queryClient.setQueryData(key, value))
    },
    onSuccess: () => {
      invalidateVisioReviewData(queryClient)
    },
  })
}

export const useVisioReviewConfig = () =>
  useQuery({
    queryKey: ['visio-reviews', 'config'],
    queryFn: fetchVisioReviewConfig,
    staleTime: STALE_TIME,
  })

export const useUpdateVisioReviewConfig = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<VisioReviewConfig>) => updateVisioReviewConfig(data),
    onSuccess: (data) => {
      queryClient.setQueryData(['visio-reviews', 'config'], data)
    },
  })
}

export const useVisioReviewInteractionsToEvaluate = (
  params: FetchVisioReviewInteractionsToEvaluateParams,
) =>
  useInfiniteQuery({
    queryKey: ['visio-reviews', 'to-evaluate', params],
    queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
      fetchVisioReviewInteractionsToEvaluate({ ...params, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: hasMorePageParam,
    staleTime: STALE_TIME,
  })

export const useValidateVisioReviewAiGrade = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (reviewId: number) => validateVisioReviewAiGrade(reviewId),
    onMutate: async (reviewId) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: ['visio-reviews', 'operator-interactions'] }),
        queryClient.cancelQueries({ queryKey: ['visio-reviews', 'store-interactions'] }),
      ])
      const previous = [
        ...queryClient.getQueriesData<OperatorInteractionsCache>({
          queryKey: ['visio-reviews', 'operator-interactions'],
        }),
        ...queryClient.getQueriesData<OperatorInteractionsCache>({
          queryKey: ['visio-reviews', 'store-interactions'],
        }),
      ]
      patchOperatorInteractionsCache(queryClient, reviewId, { aiGradeValidatedBy: -1 })
      return { previous }
    },
    onError: (_err, _vars, context) => {
      context?.previous.forEach(([key, value]) => queryClient.setQueryData(key, value))
    },
    onSuccess: () => {
      invalidateVisioReviewData(queryClient)
    },
  })
}

export const useVisioReviewFilterOptions = (params: FetchVisioReviewFilterOptionsParams) =>
  useQuery({
    queryKey: ['visio-reviews', 'filter-options', params],
    queryFn: () => fetchVisioReviewFilterOptions(params),
    staleTime: STALE_TIME,
  })

export const useUpdateVisioReviewResolution = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      reviewId,
      data,
    }: {
      reviewId: number
      data: { resolutionStatus: ResolutionStatus }
    }) => updateVisioReviewResolution(reviewId, data),
    onMutate: async ({ reviewId, data }) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: ['visio-reviews', 'operator-interactions'] }),
        queryClient.cancelQueries({ queryKey: ['visio-reviews', 'store-interactions'] }),
      ])
      const previous = [
        ...queryClient.getQueriesData<OperatorInteractionsCache>({
          queryKey: ['visio-reviews', 'operator-interactions'],
        }),
        ...queryClient.getQueriesData<OperatorInteractionsCache>({
          queryKey: ['visio-reviews', 'store-interactions'],
        }),
      ]
      patchOperatorInteractionsCache(queryClient, reviewId, {
        resolutionStatus: data.resolutionStatus,
      })
      return { previous }
    },
    onError: (_err, _vars, context) => {
      context?.previous.forEach(([key, value]) => queryClient.setQueryData(key, value))
    },
    onSuccess: () => {
      invalidateVisioReviewData(queryClient)
    },
  })
}
